// =====================================================
// Changed numbers, amounts, percentages and dates, for the summary page (every output).
//
// For each paragraph with changes, find the figures in the original and revised text
// (dates, money, percentages, other numbers, including "thirty (30)" and "forty-five")
// that a change touches, then pair old with new by kind and order. A figure that only
// exists on one side (deleted or added) gets a row with the other side empty.
// =====================================================

export type FigureKind = 'Date' | 'Amount' | 'Percentage' | 'Number';
export interface Figure { kind: FigureKind; text: string; start: number; end: number; }
export interface FigureRow { kind: FigureKind; old: string; new: string; block: number | null; where: string; }

const MONTH = '(?:January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\\.?';
const ORD = '(?:st|nd|rd|th)?';
const NUM_WORD = '(?:zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|thousand|million|billion)';
const NUMBER_WORDS = `${NUM_WORD}(?:[- ]${NUM_WORD})*`;
const DIGITS = '\\d+(?:[.,]\\d+)*';

// Most specific first; later patterns skip characters an earlier match already claimed.
const PATTERNS: [FigureKind, RegExp][] = [
  ['Date', new RegExp(`\\b${MONTH}\\s+\\d{1,2}${ORD},?\\s+\\d{4}\\b`, 'g')],
  ['Date', new RegExp(`\\b\\d{1,2}${ORD}\\s+(?:day\\s+of\\s+)?${MONTH},?\\s+\\d{4}\\b`, 'g')],
  ['Date', /\b\d{4}-\d{2}-\d{2}\b/g],
  ['Date', /\b\d{1,2}[/.]\d{1,2}[/.]\d{2,4}\b/g],
  ['Date', new RegExp(`\\b${MONTH}\\s+\\d{4}\\b`, 'g')],
  ['Date', new RegExp(`\\b${MONTH}\\s+\\d{1,2}${ORD}\\b`, 'g')],
  ['Amount', new RegExp(`(?:US|U\\.S\\.)?[$€£]\\s?${DIGITS}(?:\\s?(?:million|billion|thousand|MM|mm|M|B|K)\\b)?`, 'g')],
  ['Amount', new RegExp(`\\b${DIGITS}\\s?(?:dollars|USD|EUR|GBP)\\b`, 'gi')],
  ['Percentage', new RegExp(`(?:\\b${NUMBER_WORDS}\\s+)?\\b${DIGITS}\\s?(?:%|percent\\b|per cent\\b)|\\b${NUMBER_WORDS}\\s+(?:percent|per cent)\\b(?:\\s*\\(${DIGITS}\\s?%\\))?`, 'gi')],
  ['Number', new RegExp(`\\b${NUMBER_WORDS}\\s*\\(${DIGITS}\\)`, 'gi')],
  ['Number', new RegExp(`\\(?\\b${DIGITS}\\b\\)?`, 'g')],
  ['Number', new RegExp(`\\b${NUMBER_WORDS}\\b`, 'gi')],
];

/** Every figure in a text, left to right, non-overlapping. */
export function findFigures(text: string): Figure[] {
  const taken = new Array(text.length).fill(false);
  const out: Figure[] = [];
  for (const [kind, re] of PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      let s = m.index, e = s + m[0].length;
      // keep parentheses only when balanced: "(30)" and "thirty (30)" whole, "(30" → "30"
      const opens = (m[0].match(/\(/g) || []).length, closes = (m[0].match(/\)/g) || []).length;
      if (opens > closes && m[0][0] === '(') s++;
      else if (closes > opens && m[0].endsWith(')')) e--;
      if (e <= s) continue;
      let free = true;
      for (let i = s; i < e; i++) if (taken[i]) { free = false; break; }
      if (!free) continue;
      for (let i = s; i < e; i++) taken[i] = true;
      out.push({ kind, text: '', start: s, end: e });
    }
  }
  // Context that says what a figure is: "Section 4.2", "thirty (30) days", "$8,750 per month".
  for (const f of out) {
    if (f.kind === 'Number') {
      const before = REF_BEFORE.exec(text.slice(Math.max(0, f.start - 24), f.start));
      if (before) f.start -= before[0].length;
      const after = UNIT_AFTER.exec(text.slice(f.end, f.end + 30));
      if (after) f.end += after[0].length;
    } else if (f.kind === 'Amount') {
      const after = PER_AFTER.exec(text.slice(f.end, f.end + 30));
      if (after) f.end += after[0].length;
    }
    f.text = text.slice(f.start, f.end).replace(/\s+/g, ' ').trim();
  }
  return out.sort((a, b) => a.start - b.start);
}

const REF_BEFORE = /(?:\b(?:Sections?|Articles?|Exhibits?|Schedules?|Annex(?:es)?|Appendi(?:x|ces)|Clauses?|Paragraphs?|Subsections?|Lots?|Suites?|Units?|Items?|Rules?|Parts?|Nos?\.)|§§?)\s*$/;
const UNIT_AFTER = /^\s+(?:(?:business|calendar|working|banking)\s+days?|days?|weeks?|months?|years?|hours?|minutes?|(?:(?:rentable|usable|gross|net)\s+)?square\s+feet|feet|foot|acres?|miles?|shares?|units?|kW|MW|MWh|kWh|GW|tons?|gallons?)\b/i;
const PER_AFTER = /^\s+(?:per|a|each)\s+(?:month|year|annum|week|day|hour|square\s+foot|unit|share|MWh|kWh)\b/i;

const overlaps = (f: Figure, ranges: [number, number][]) => ranges.some(([s, e]) => f.start < e && s < f.end);
const same = (a: string, b: string) => a.replace(/[\s,]/g, '').toLowerCase() === b.replace(/[\s,]/g, '').toLowerCase();

/**
 * Rows for one paragraph: figures in `oldText` touched by `oldRanges` and figures in
 * `newText` touched by `newRanges`, paired by kind in order of appearance.
 */
export function changedFigures(oldText: string, oldRanges: [number, number][], newText: string, newRanges: [number, number][],
  block: number | null, where: string): FigureRow[] {
  // not list markers ("(1)", "(b)") or a typed heading number: they are not terms
  const olds = oldRanges.length ? findFigures(oldText).filter(f => overlaps(f, oldRanges) && !typedNumber(f, oldText) && !ENUMERATOR.test(f.text)) : [];
  const news = newRanges.length ? findFigures(newText).filter(f => overlaps(f, newRanges) && !typedNumber(f, newText) && !ENUMERATOR.test(f.text)) : [];
  const rows: FigureRow[] = [];
  for (const kind of ['Date', 'Amount', 'Percentage', 'Number'] as FigureKind[]) {
    const o = olds.filter(f => f.kind === kind), n = news.filter(f => f.kind === kind);
    const paired = Math.min(o.length, n.length);
    // a figure replaced by another: one row each
    for (let i = 0; i < paired; i++) {
      if (same(o[i].text, n[i].text)) continue;
      rows.push({ kind, old: o[i].text, new: n[i].text, block, where });
    }
    // figures only removed or only added (a deleted or new passage): one row per side
    const keep = (f: Figure) => !BARE_SMALL.test(f.text);
    const onlyOld = list(o.slice(paired).filter(keep)), onlyNew = list(n.slice(paired).filter(keep));
    if (onlyOld) rows.push({ kind, old: onlyOld, new: '', block, where });
    if (onlyNew) rows.push({ kind, old: '', new: onlyNew, block, where });
  }
  return rows;
}

/** "$50,000 ×3, $125,000": each figure once, in order, with how often it appears. */
function list(fs: Figure[]): string {
  const counts = new Map<string, number>();
  for (const f of fs) counts.set(f.text, (counts.get(f.text) || 0) + 1);
  return [...counts].map(([t, k]) => k > 1 ? `${t} ×${k}` : t).join(', ');
}

const ENUMERATOR = /^\((?:\d{1,2}|[a-z]{1,3})\)$/i;
// a number with nothing to say what it counts ("3", "12", "one"): a list or heading number, a page
// reference. Shown when it replaces another number, not on its own.
const BARE_SMALL = new RegExp(`^(?:\\d{1,2}|${NUMBER_WORDS})$`, 'i');
/** A heading or list number typed at the start of a paragraph ("6.", "2.1", "(b)"): renumbering, not a term. */
const typedNumber = (f: Figure, text: string) => f.kind === 'Number' && !text.slice(0, f.start).trim() && /^\(?\d+(?:\.\d+)*[.)]?$/.test(f.text);
